/* cf-job.js — Click Fencing, 2026-09-23. PHASE 1 of mixed fence types.
 *
 * WHAT THIS IS. A customer can price glass along the pool and mesh along the boundary,
 * and buy both as ONE order. Each calculator still saves its own quote exactly as it
 * always has; this asset holds a JOB that REFERENCES those saved quotes ("runs") and
 * merges them into one quote object for /pages/quote-review and the cart.
 *
 * WHAT IT DOES NOT DO. It never edits the four calculator sources, never writes to the
 * four saved-quote lists, and never invents a price. Every dollar in a merged job comes
 * from a line one of the calculators produced.
 *
 * 🔴 DELIVERY AND TAX — the two things a naive sum gets wrong.
 * A saved quote's grandTotal ALREADY CONTAINS that run's own delivery charge, so adding
 * two grandTotals together charges delivery twice. Some calculators have also carried
 * their own tax line at times (the glass one did until cf-gst-inclusive.js stripped it).
 * A job therefore takes each run apart and rebuilds the job-level lines once:
 *
 *     run kit  = grandTotal - that run's tax lines - that run's delivery lines
 *     job kit  = sum of run kits
 *     job delivery = ONE line: pickup if every run picked up, free when the job kit is
 *                    $3,000 or more, otherwise the dearest run's own charge — never a
 *                    figure this file invented
 *     job total (grandTotal) = job kit + job delivery, carrying GST exactly as the runs
 *                    do, so nothing downstream needs a special case for a job.
 *
 * With that convention the existing consumers stay correct as they are:
 *   - cf-gst-display.js derives the GST split from the job total, exactly as it does for
 *     one fence (the store currently runs taxesIncluded = true, so it splits it out).
 *   - cf-real-line-items.js drops delivery and tax lines from the cart and subtracts them
 *     from its reconciliation target, so the cart holds the kit and Shopify adds shipping.
 * Both read the quote by the active pill's label, which cannot identify a job, so each
 * asks CF_JOB first — see the small patches in them.
 *
 * A job needs TWO OR MORE runs to exist. One run is an ordinary quote and every screen
 * behaves exactly as it did before this file shipped.
 *
 * Self-installing, idempotent, every path wrapped: a fault here must never break a quote.
 */
(function () {
  'use strict';
  if (window.CF_JOB) return;

  var KEY = 'cf_job';
  var CALC_KEYS = {
    Frameless: { quotes: 'ullc_quotes',     active: 'ullc_active_quote'     },
    PerfMesh:  { quotes: 'perfmesh_quotes', active: 'perfmesh_active_quote' },
    FlatTop:   { quotes: 'flattop_quotes',  active: 'flattop_active_quote'  },
    Blade:     { quotes: 'blade_quotes',    active: 'blade_active_quote'    }
  };
  var FENCE_LABEL = {
    Frameless: 'Frameless Glass',
    PerfMesh:  'PerfMesh',
    FlatTop:   'Flat Top',
    Blade:     'Blade'
  };
  var CALC_PATH = {
    Frameless: '/pages/glass-fencing',
    PerfMesh:  '/pages/perfmesh-fence',
    FlatTop:   '/pages/flat-top-fence-calculator',
    Blade:     '/pages/blade-fence-calculator'
  };
  /* Pete, 22 Sep 2026: FREE Perth metro delivery on orders over $3,000. Shopify's own
     local-delivery zone decides it at checkout from the cart subtotal, and
     cf-free-delivery.js decides it inside each calculator from that calculator's own
     subtotal. The job compares the same kind of figure — the runs' own kit totals added
     up — so the calculator, the job and the checkout all agree. */
  var FREE_DELIVERY_FROM_CENTS = 300000;
  var GST_RATE = 0.10;

  function log() {
    try { console.log.apply(console, ['[cf-job]'].concat([].slice.call(arguments))); } catch (e) {}
  }
  function cents(n) { return Math.round((Number(n) || 0) * 100); }
  function dollars(c) { return Math.round(Number(c) || 0) / 100; }

  /* ── storage ───────────────────────────────────────────────────────────────── */

  function blank() {
    return {
      v: 1,
      id: 'job-' + Date.now().toString(36),
      createdAt: new Date().toISOString(),
      on: false,
      runs: [],          // [{ calcType, quoteNum }] — references only, never copies
      look: null,        // phase 2: { id, glass, alu }
      fixing: null,      // phase 2: 'base' | 'ground' | { <calcType>: 'base'|'ground' }
      plan: null         // phase 3 (2026-09-24): { items: [{planNum, calcType, lengthMm}], updatedAt }
    };
  }

  function state() {
    var j = null;
    try { j = JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (e) {}
    if (!j || typeof j !== 'object' || !Array.isArray(j.runs)) j = blank();
    return j;
  }

  function save(j) {
    try { localStorage.setItem(KEY, JSON.stringify(j)); } catch (e) {}
    try {
      document.documentElement.setAttribute('data-cf-job', isOn() ? 'on' : 'off');
    } catch (e) {}
    try { document.dispatchEvent(new CustomEvent('cf-job:changed')); } catch (e) {}
    return j;
  }

  /* ── the saved quotes a job can be built from ──────────────────────────────── */

  function availableRuns() {
    var out = [];
    Object.keys(CALC_KEYS).forEach(function (calcType) {
      var arr = [];
      try { arr = JSON.parse(localStorage.getItem(CALC_KEYS[calcType].quotes) || '[]') || []; } catch (e) {}
      if (Object.prototype.toString.call(arr) !== '[object Array]') return;
      arr.forEach(function (q) {
        if (!q || !q.breakdown || !q.breakdown.length) return;
        var copy = Object.assign({}, q);
        if (!copy.calcType) copy.calcType = calcType;
        out.push(copy);
      });
    });
    out.sort(function (a, b) {
      return (Date.parse(b.generatedAt) || 0) - (Date.parse(a.generatedAt) || 0);
    });
    return out;
  }

  function findRun(calcType, quoteNum) {
    var all = availableRuns();
    for (var i = 0; i < all.length; i++) {
      if (String(all[i].calcType) === String(calcType) && Number(all[i].quoteNum) === Number(quoteNum)) return all[i];
    }
    return null;
  }

  /* The runs currently in the job, in the order they were added, dropping any whose
     saved quote has since been deleted. */
  function includedQuotes() {
    var j = state(), out = [];
    j.runs.forEach(function (r) {
      var q = findRun(r.calcType, r.quoteNum);
      if (q) out.push(q);
    });
    /* 2026-09-26: in a planned job, fences come in the customer's own section order
       (Glass S1 before Blade S2), not the order they happened to be saved in. */
    try {
      var p = j.plan && j.plan.items && j.plan.items.length ? j.plan.items.slice().sort(function (a, b) { return a.planNum - b.planNum; }) : null;
      if (p) {
        var first = {};
        p.forEach(function (it, i) { if (first[it.calcType] == null) first[it.calcType] = i; });
        out.sort(function (a, b) { return (first[a.calcType] != null ? first[a.calcType] : 99) - (first[b.calcType] != null ? first[b.calcType] : 99); });
      }
    } catch (e) {}
    return out;
  }

  /* 26 Sep review: a job is only "on" in the browser tab that is building it. The job sits in
     localStorage for good, so an old combined job left on used to be merged into a brand-new
     single fence on the quote page, weeks later, and its fences sent to the cart. */
  var ACTIVE_KEY = 'cf_job_active';
  function tabActive() {
    try { return sessionStorage.getItem(ACTIVE_KEY) === '1'; } catch (e) { return false; }
  }
  function isOn() {
    var j = state();
    return !!j.on && tabActive() && includedQuotes().length >= 2;
  }

  function hasRun(calcType, quoteNum) {
    return state().runs.some(function (r) {
      return String(r.calcType) === String(calcType) && Number(r.quoteNum) === Number(quoteNum);
    });
  }

  function addRun(calcType, quoteNum) {
    var j = state();
    if (!hasRun(calcType, quoteNum)) j.runs.push({ calcType: calcType, quoteNum: Number(quoteNum), addedAt: new Date().toISOString() });
    return save(j);
  }

  /* 2026-09-26 — a planned mixed job has ONE run per fence type (the plan puts every section of
     a type in that one calculator). Going back to a fence and saving it again must REPLACE its
     run, never add a second one — measured on preview 157547069534: glass saved, Blade opened,
     back to glass, saved again = two glass runs and the glass money counted twice. */
  function replaceRun(calcType, quoteNum) {
    var j = state();
    var fresh = { calcType: calcType, quoteNum: Number(quoteNum), addedAt: new Date().toISOString() };
    var at = -1;
    j.runs.forEach(function (r, i) { if (at < 0 && String(r.calcType) === String(calcType)) at = i; });
    j.runs = j.runs.filter(function (r) { return String(r.calcType) !== String(calcType); });
    if (at < 0 || at > j.runs.length) j.runs.push(fresh); else j.runs.splice(at, 0, fresh);   // keeps the fence in its place in the strip
    return save(j);
  }

  function removeRun(calcType, quoteNum) {
    var j = state();
    j.runs = j.runs.filter(function (r) {
      return !(String(r.calcType) === String(calcType) && Number(r.quoteNum) === Number(quoteNum));
    });
    return save(j);
  }

  function toggleRun(calcType, quoteNum) {
    return hasRun(calcType, quoteNum) ? removeRun(calcType, quoteNum) : addRun(calcType, quoteNum);
  }

  function setOn(on) {
    var j = state();
    j.on = !!on;
    if (on) try { sessionStorage.setItem(ACTIVE_KEY, '1'); } catch (e) {}   // switched on here = this tab's job
    return save(j);
  }

  function clear() {
    var j = blank();
    return save(j);
  }

  /* ── PLAN (2026-09-24) — the customer's own section order, chosen where sections are
     added, not discovered afterwards. { items: [{planNum, calcType, lengthMm}] }, in the
     order the customer entered them at the FIRST calculator. cf-job-ui.js owns writing it;
     this file only stores it and uses it to relabel a merged job's sections with the
     customer's original numbers. Never required — every function below is safe with no
     plan at all, which is every job before 2026-09-24. */

  function getPlan() {
    var j = state();
    return (j.plan && Object.prototype.toString.call(j.plan.items) === '[object Array]' && j.plan.items.length) ? j.plan : null;
  }
  function savePlan(items) {
    var j = state();
    j.plan = { items: items || [], updatedAt: new Date().toISOString() };
    return save(j);
  }
  function clearPlan() {
    var j = state();
    j.plan = null;
    return save(j);
  }
  /* This calc type's items, in the plan's own order — the order the customer entered them. */
  function planItemsForCalc(calcType) {
    var p = getPlan();
    if (!p) return [];
    return p.items.filter(function (it) { return it.calcType === calcType; })
                   .sort(function (a, b) { return a.planNum - b.planNum; });
  }
  /* Every calc type in the plan, in first-appearance order. */
  function planCalcOrder() {
    var p = getPlan();
    if (!p) return [];
    var seen = [], out = [];
    p.items.slice().sort(function (a, b) { return a.planNum - b.planNum; }).forEach(function (it) {
      if (seen.indexOf(it.calcType) < 0) { seen.push(it.calcType); out.push(it.calcType); }
    });
    return out;
  }
  /* The next calc type the plan calls for that the job does not already have a run of —
     `afterType` (normally "the fence I am on right now") is never returned, so this can
     never recommend hopping to the fence the customer is already standing on. Null once
     every fence type in the plan is in the job. */
  function planNextCalc(afterType) {
    var order = planCalcOrder();
    if (!order.length) return null;
    var have = {};
    includedQuotes().forEach(function (q) { have[q.calcType] = true; });
    for (var i = 0; i < order.length; i++) {
      if (order[i] === afterType) continue;
      if (!have[order[i]]) return order[i];
    }
    return null;
  }

  /* ── line classification: identical tests to cf-real-line-items.js ─────────── */

  function isTax(b) {
    return /^tax$/i.test(String(b.category || '').trim()) || /\bgst\b/i.test(String(b.item || ''));
  }
  function isDelivery(b) {
    return /^delivery$/i.test(String(b.category || '').trim());
  }
  function isPickupLine(b) {
    return /pickup/i.test(String(b.item || ''));
  }

  function runLengthMm(q) {
    return (q.sections || []).reduce(function (s, sec) { return s + (parseFloat(sec.length) || 0); }, 0);
  }

  function runLabel(q) {
    var name = FENCE_LABEL[q.calcType] || q.calcType || 'Fence';
    return name + ' · ' + (runLengthMm(q) / 1000).toFixed(1) + ' m';
  }

  /* ── the merge ─────────────────────────────────────────────────────────────── */

  function merge(quotesIn) {
    var quotes = quotesIn || includedQuotes();
    if (quotes.length < 2) return null;

    var kitCents = 0, deliveryCents = 0, anyDelivery = false, allPickup = true, badRun = false;
    var breakdown = [], sections = [], runs = [];
    var newest = 0, anyInstall = false, currency = 'AUD';

    quotes.forEach(function (q, idx) {
      var label = runLabel(q);
      var taxC = 0, delC = 0, sawDelivery = false, sawPickup = false;

      (q.breakdown || []).forEach(function (b) {
        if (isTax(b)) { taxC += cents(b.totalPrice); return; }
        if (isDelivery(b)) {
          sawDelivery = true;
          if (isPickupLine(b)) sawPickup = true;
          delC += cents(b.totalPrice);
          return;
        }
        if (/professional installation/i.test(String(b.category || ''))) anyInstall = true;
        breakdown.push(Object.assign({}, b, { _run: idx, _runLabel: label, _runCalcType: q.calcType }));
      });

      /* The run's KIT, ex GST and ex delivery — the only figure that can be added up.
         A quote whose total is not a real positive number cannot be part of a job: the
         calculators never emit one, and a job is not the place to discover otherwise. */
      var runTotal = Number(q.grandTotal);
      if (!isFinite(runTotal) || runTotal <= 0) {
        log('run refused, total is not a usable amount', q.calcType, q.quoteNum, q.grandTotal);
        badRun = true;
      }
      var runKit = cents(q.grandTotal) - taxC - delC;
      kitCents += runKit;

      if (sawDelivery) {
        anyDelivery = true;
        if (!sawPickup) allPickup = false;
        if (delC > deliveryCents) deliveryCents = delC;   // dearest run's charge, never invented
      } else {
        allPickup = false;
      }

      /* PLAN-aware section numbers (2026-09-24): if the customer chose fence types at
         Add Sections, this run's own sections were numbered 1..M on ITS calculator, but
         the customer thinks of them by the PLAN's whole-job numbers. Map local order to
         the plan's numbers for this calc type when the counts agree; otherwise fall back
         to the run's own numbering exactly as before — a plan is never required. */
      var planForRun = planItemsForCalc(q.calcType);
      var usePlanNums = planForRun.length === (q.sections || []).length && planForRun.length > 0;
      (q.sections || []).forEach(function (s, sIdx) {
        var displayNum = usePlanNums ? planForRun[sIdx].planNum : (s.num != null ? s.num : (sections.length + 1));
        sections.push(Object.assign({}, s, {
          calcType: q.calcType,
          label: (FENCE_LABEL[q.calcType] || q.calcType) + ' · Section ' + displayNum,
          planNum: usePlanNums ? planForRun[sIdx].planNum : null,
          _run: idx,
          _runLabel: label
        }));
      });

      if (q.pricingType === 'install') anyInstall = true;
      if (q.currency) currency = q.currency;
      var t = Date.parse(q.generatedAt) || 0;
      if (t > newest) newest = t;

      runs.push({
        calcType:  q.calcType,
        quoteNum:  q.quoteNum,
        quoteName: q.quoteName || ('Quote ' + q.quoteNum),
        label:     label,
        fence:     FENCE_LABEL[q.calcType] || q.calcType,
        lengthMm:  runLengthMm(q),
        sections:  (q.sections || []).length,
        gates:     (q.sections || []).filter(function (s) { return s.hasGate; }).length,
        kitTotal:  dollars(runKit),
        path:      CALC_PATH[q.calcType] || '/pages/pool-fencing-options'
      });
    });

    /* ONE delivery line for the whole job, judged on the whole job's kit. */
    var deliveryItem = null;
    if (allPickup && anyDelivery) {
      deliveryCents = 0;
      deliveryItem = { category: 'Delivery', item: 'Free Pickup from our Canning Vale Warehouse', quantity: 1, unitPrice: 0, totalPrice: 0 };
    } else if (anyDelivery) {
      if (kitCents >= FREE_DELIVERY_FROM_CENTS) {
        deliveryCents = 0;
        deliveryItem = { category: 'Delivery', item: 'Perth Metro Delivery — FREE (job over $3,000)', quantity: 1, unitPrice: 0, totalPrice: 0 };
      } else {
        deliveryItem = { category: 'Delivery', item: 'Perth Metro Delivery', quantity: 1, unitPrice: dollars(deliveryCents), totalPrice: dollars(deliveryCents) };
      }
    } else {
      deliveryCents = 0;
    }
    if (deliveryItem) breakdown.push(deliveryItem);

    /* The job total carries GST exactly as the runs do — whatever the store's
       taxesIncluded setting says today — because it is built by adding up the runs'
       own figures and never re-bases them. No tax line is added here: one would make
       this the only quote on the site whose grandTotal means something different from
       every other, and cf-gst-display.js already derives the split for the page. */
    var subtotalCents = kitCents + deliveryCents;
    /* Fail safe, not loud: with no job the page simply shows the single quotes it always
       did, which is the correct behaviour for a job that cannot be priced. */
    if (badRun || !isFinite(subtotalCents) || subtotalCents <= 0) {
      log('job refused, total is not a usable amount:', subtotalCents);
      return null;
    }
    var gstCents = Math.round(subtotalCents * (window.CF_TAXES_INCLUDED === true ? (1 / 11) : GST_RATE));

    var j = state();
    /* Sections read S1, S2, S3 in the customer's order when every one has a plan number. */
    if (sections.length && sections.every(function (s) { return s.planNum != null; })) {
      sections.sort(function (a, b) { return a.planNum - b.planNum; });
    }

    var fenceNames = runs.map(function (r) { return r.fence; });
    var uniqueFences = fenceNames.filter(function (f, i) { return fenceNames.indexOf(f) === i; });

    return {
      isJob:       true,
      jobId:       j.id,
      calcType:    'Mixed',
      runs:        runs,
      fences:      uniqueFences,
      quoteName:   'Your fence job — ' + uniqueFences.join(' + '),
      quoteNum:    j.id,
      pricingType: anyInstall ? 'install' : 'diy',
      currency:    currency,
      sections:    sections,
      breakdown:   breakdown,
      kitTotal:      dollars(kitCents),
      deliveryTotal: dollars(deliveryCents),
      gstContent:    dollars(gstCents),        // informational only
      grandTotal:    dollars(subtotalCents),   // same meaning as every saved quote's
      generatedAt: newest ? new Date(newest).toISOString() : new Date().toISOString(),
      look:        j.look || null,
      fixing:      j.fixing || null
    };
  }

  /* The merged quote as the CART and the GST split must see it — null unless a real job
     is on, so every non-job screen keeps its old behaviour untouched. */
  function activeQuote() {
    try { return isOn() ? merge() : null; } catch (e) { log('merge failed', e); return null; }
  }

  try {
    document.documentElement.setAttribute('data-cf-job', isOn() ? 'on' : 'off');
  } catch (e) {}

  window.CF_JOB = {
    version:       1,
    KEY:           KEY,
    CALC_KEYS:     CALC_KEYS,
    FENCE_LABEL:   FENCE_LABEL,
    CALC_PATH:     CALC_PATH,
    state:         state,
    save:          save,
    clear:         clear,
    isOn:          isOn,
    setOn:         setOn,
    hasRun:        hasRun,
    addRun:        addRun,
    replaceRun:    replaceRun,
    removeRun:     removeRun,
    toggleRun:     toggleRun,
    availableRuns: availableRuns,
    includedQuotes: includedQuotes,
    runLabel:      runLabel,
    merge:         merge,
    activeQuote:   activeQuote,
    getPlan:       getPlan,
    savePlan:      savePlan,
    clearPlan:     clearPlan,
    planItemsForCalc: planItemsForCalc,
    planCalcOrder: planCalcOrder,
    planNextCalc:  planNextCalc
  };

  /* PHASE 1 ONLY — "Adjust your kit" (cf-quote-editor.js) binds to ONE saved quote by the
     active pill's label and writes back to it. In a job there is no single such quote, so
     it would edit whichever quote it matched while the customer looks at the job total.
     Hiding it is the safe answer until per-run editing lands in phase 2. */
  try {
    var st = document.createElement('style');
    st.id = 'cf-job-css';
    st.textContent = [
      'html[data-cf-job="on"] .cf-qe{display:none !important}',
      /* cf-plans.js stamps ONE run's plan reference under the title; in a job that names
         one fence of several. The job's own reference is minted at checkout (CF-MIX-…). */
      'html[data-cf-job="on"] #cf-plan-ref{display:none !important}',
      '.qr-job-toggle{display:inline-flex;align-items:center;gap:8px;margin-left:auto;',
      'font-weight:600;color:#374151;cursor:pointer;font-size:.9rem;padding:4px 0}',
      '.qr-job-toggle input{width:17px;height:17px;accent-color:#0b3d20;cursor:pointer;margin:0}',
      '.qr-job-toggle__hint{color:#64748b;font-weight:400;font-size:.78rem}',
      '.qr-quote-pill .qr-pill-tick{display:inline-block;min-width:.9em;margin-right:5px;font-weight:700}',
      '.qr-quote-pill.is-in-job{border-color:#0b3d20}',
      '@media (max-width:640px){.qr-job-toggle{margin-left:0;width:100%}',
      '.qr-job-toggle__hint{display:block}}'
    ].join('');
    (document.head || document.documentElement).appendChild(st);
  } catch (e) {}

  log('installed');
})();
